import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
  Inject,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Actions, ofActionCompleted, ofActionSuccessful, Store } from '@ngxs/store';

import { ObjectsManagerActions } from '../../state/actions';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { TsSnackbarService } from 'tsui';

@Component({
  selector: 'add-object',
  templateUrl: './add-object.component.html',
  styleUrls: ['./add-object.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddObjectComponent extends OnDestroyMixin implements OnInit {
  addObjectForm: FormGroup;
  isLoading: boolean;
  nameError: string;
  customObject: CustomObject;

  get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.addObjectForm.pristine || this.addObjectForm.invalid;
  }

  get labelControl(): AbstractControl {
    return this.addObjectForm.get('label');
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<AddObjectComponent>,
    private readonly snackBar: TsSnackbarService,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.customObject = this.data?.customObject;
    this.buildForm();

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionSuccessful(ObjectsManagerActions.Create))
      .subscribe(() => {
        this.isLoading = false;
        this.snackBar.open('Object has been created.', 'success');
        this.close();
      });

    this.actions$.pipe(untilComponentDestroyed(this), ofActionSuccessful(ObjectsManagerActions.Patch)).subscribe(() => {
      this.isLoading = false;
      this.close();
    });

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionCompleted(ObjectsManagerActions.Create))
      .subscribe(({ result }) => {
        if (result.error && result.error instanceof HttpErrorResponse) {
          this.nameError = result.error.error.custom_object.errors.join(' ');
          this.addObjectForm.enable();
          this.labelControl.setErrors({ invalidCOName: true });
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
        }
      });

    this.labelControl.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.labelControl.markAsTouched());
  }

  private buildForm(): void {
    this.addObjectForm = new FormGroup({
      label: new FormControl('', {
        validators: [Validators.required],
      }),
      label_plural: new FormControl(''),
    });

    if (this.customObject) {
      this.addObjectForm.get('label').setValue(this.customObject.label);
      this.addObjectForm.get('label_plural').setValue(this.customObject.label_plural);
    }
  }

  saveAndClose(): void {
    this.addObjectForm.disable();
    this.isLoading = true;
    const newObject = this.addObjectForm.value;
    if (this.customObject) {
      this.store.dispatch(new ObjectsManagerActions.Patch(this.customObject.id, this.addObjectForm.value));
    } else {
      this.store.dispatch(new ObjectsManagerActions.Create(newObject));
    }
  }

  close(): void {
    this.matDialogRef.close();
  }

  isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  private nameValidator(control: AbstractControl): ValidationErrors {
    if (!/^[a-zA-Z0-9-_ ]+$/gi.test(control.value) || control.value.indexOf(' ') >= 0) {
      return { invalidCOName: true };
    }
    return null;
  }
}
